import { aoFicarInvalido, aoMexer } from "../../components/avisos-em-portugues";
import { FormEvent, useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

import "./login.scss";
// T8.21 · as classes de botão do cadastro. Importar a folha é o que faz "os mesmos componentes" ser
// verdade em vez de ser uma cópia das regras com os mesmos valores — cópia diverge no primeiro ajuste.
import "../sign-up/sign-up.scss";
import Nav from "../../components/nav";
import Firebase from "../../api/firebase.class";
import { useGlobalState } from "../../global-state-context";
import { SENHA } from "../sign-up/aceite-e-idade";
import {
  Campo,
  Recado,
  recadoDeEntrada,
  recadoDaTroca,
  recadoDeRecuperacao,
  RECUPERACAO_ENVIADA,
} from "./recado-do-login";

function Login(): JSX.Element {
  const navigate = useNavigate();
  const local = useLocation();

  /**
   * DE ONDE A PESSOA VEIO, E POR QUE ELA ESTÁ AQUI.
   *
   * T2.18 do pedido, com o sentido que o bloco inteiro dá (a frase sai com a negação invertida e
   * o dossiê marca isso): "deveria ir para área de login para DEPOIS ir para parte de criar
   * quadrinho".
   *
   * Antes, entrar levava SEMPRE para `/meus-quadrinhos`. Para quem clicou em "criar" isso
   * acertava por coincidência; para quem clicou em qualquer outra coisa, errava. E quem foi
   * barrado não era mandado para cá: era mandado para a HOME, o que apagava a intenção inteira —
   * é o T2.17 e o T2.19, que são o mesmo salto visto de dois lugares.
   *
   * `state` do roteador e não parâmetro de URL, de propósito: destino de volta em query string é
   * um convite a redirecionamento aberto, e um dia alguém cola um endereço de fora ali.
   */
  const vindoDe = local.state as { voltarPara?: string; motivo?: string } | null;
  const voltarPara =
    // `startsWith("/")` e sem "//": só caminho interno. `//host` é endereço absoluto de outro
    // site, e o navegador o trata como tal.
    typeof vindoDe?.voltarPara === "string" &&
    vindoDe.voltarPara.startsWith("/") &&
    !vindoDe.voltarPara.startsWith("//")
      ? vindoDe.voltarPara
      : "/meus-quadrinhos";
  const globalState = useGlobalState();

  const [isAwaitingAsyncEvent, setIsAwaitingAsyncEvent] = useState(false);
  const [user, setUser] = useState("");
  const [password, setPassword] = useState("");
  const [isResetingPassword, setIsResetingPassword] = useState(false);
  // T29.5 · o e-mail novo, para quem errou o endereço no cadastro.
  const [trocandoOEmail, setTrocandoOEmail] = useState(false);
  const [novoEmail, setNovoEmail] = useState("");

  // ── O RETORNO DA TELA — T8.15 e T8.31 ────────────────────────────────────────────────────
  //
  // Esta tela tinha **14 chamadas de `alert()`** e nenhuma outra forma de dizer o que aconteceu.
  // A sugestão do relatório pede o modal do site; a emenda do achado pede mais, e é o que vale:
  // **o erro junto ao campo**. Um diálogo — nativo ou bonito — obriga a ler, fechar, e só então
  // voltar ao campo, que continua sem marca nenhuma de que foi ele o problema.
  //
  // Quem decide a frase e o campo é `recado-do-login.ts`, provado em teste: é lá que está a regra
  // de não distinguir "conta não existe" de "senha errada", e a de a recuperação responder o mesmo
  // para e-mail sem conta.
  const [recado, setRecado] = useState<Recado | null>(null);

  function limpaRecado() {
    setRecado(null);
  }

  /** O recado que pertence a este campo, ou `null`. */
  function recadoDoCampo(campo: Campo): Recado | null {
    return recado && recado.campo === campo && recado.texto ? recado : null;
  }

  function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setIsAwaitingAsyncEvent(true);
    setRecado(null);

    if (!isResetingPassword) {
      Firebase.login(user, password)
        .catch((err) => setRecado(recadoDeEntrada(err?.code ?? "")))
        .finally(() => setIsAwaitingAsyncEvent(false));
    } else {
      Firebase.sendRecoverPasswordEmail(user)
        .then(() => {
          setRecado(RECUPERACAO_ENVIADA);
          setUser("");
          setIsResetingPassword(false);
        })
        .catch(async (err) => {
          const r = recadoDeRecuperacao(err?.code ?? "");
          // O caminho que finge sucesso mantém a espera de 3 s que a tela já fazia: sem ela, um
          // e-mail sem conta responde na hora e um com conta demora, e o TEMPO passa a ser a
          // resposta que a mensagem se recusa a dar.
          if (r.fingeSucesso) {
            await new Promise((resolve) => setTimeout(resolve, 3000));
            setRecado(r);
            setUser("");
            setIsResetingPassword(false);
          } else {
            setRecado(r);
          }
        })
        .finally(() => setIsAwaitingAsyncEvent(false));
    }
  }

  function trocaDeModo() {
    if (isAwaitingAsyncEvent) return;
    setUser("");
    setPassword("");
    setRecado(null);
    setIsResetingPassword((antes) => !antes);
  }

  useEffect(() => {
    if (!globalState) return;

    if (globalState.authUser?.emailVerified) {
      navigate(voltarPara, { replace: true });
    }
  }, [navigate, globalState, voltarPara]);

  // Medido em produção em 25/09: esta aba dizia só "HQ-Lab".
  useEffect(() => {
    document.title = isResetingPassword ? "Recuperar a conta · HQ-Lab" : "Entrar · HQ-Lab";
    return () => {
      document.title = "HQ-Lab";
    };
  }, [isResetingPassword]);

  const recadoGeral = recado && recado.campo === null && recado.texto ? recado : null;

  return (
    <div className="login-container">
      <Nav />

      <form onSubmit={handleSubmit} noValidate={false} onInvalidCapture={aoFicarInvalido} onInputCapture={aoMexer}>
        {/* T8.21 · A CAIXA ALTA SAIU. Ela não é ênfase: é o texto gritando, e num produto para
            criança a diferença aparece na leitura. O cadastro, ao lado, sempre escreveu normal. */}
        <h1>{isResetingPassword ? "Recuperar a conta" : "Entrar"}</h1>

        {/* POR QUE VOCÊ ESTÁ VENDO ESTA TELA.
            Sem isto, quem clicou em "criar um quadrinho" e caiu aqui não tem como saber se
            clicou errado, se o site quebrou, ou se precisa de conta. Era o silêncio de que o
            T2.17 reclama — "nada acontece".

            E os dois casos são ditos separados porque são consertos diferentes: sem conta, a
            pessoa entra ou se cadastra; com conta e e-mail não verificado, senha nenhuma resolve
            — ela precisa abrir o e-mail. Antes, os dois recebiam a mesma tela muda. */}
        {!isResetingPassword && vindoDe?.motivo === "sem-conta" && (
          <p className="login-porque">
            Para criar e guardar quadrinhos você precisa entrar. Depois de entrar, você volta para
            onde estava.
          </p>
        )}
        {/* T8.15 · A FRASE QUE VIAJOU DA TELA DE CADASTRO.
            Lá era `alert("Conta criada, email de verificação enviado")` seguido de `navigate` — a
            frase aparecia e ia embora no mesmo gesto, e quem fechava o alerta chegava aqui sem saber
            por quê. Agora o motivo vem no estado da rota e a frase é dita ONDE a pessoa está. */}
        {!isResetingPassword && vindoDe?.motivo === "conta-criada" && (
          <p className="login-porque">
            Conta criada. Enviamos uma mensagem para o seu e-mail — abra e clique no link de
            confirmação. Depois é só entrar por aqui.
          </p>
        )}
        {!isResetingPassword && vindoDe?.motivo === "email-nao-verificado" && (
          <p className="login-porque">
            Sua conta existe, mas o e-mail ainda não foi confirmado. Abra a mensagem que enviamos
            e clique no link de confirmação — depois é só entrar.
          </p>
        )}

        {/* O recado que não é de um campo só. `role="alert"` para leitor de tela anunciar sem
            precisar de foco — que é a única coisa boa que o `alert()` fazia. */}
        {recadoGeral && (
          <p className={`login-recado login-recado-${recadoGeral.tom}`} role="alert">
            {recadoGeral.texto}
          </p>
        )}
        {recadoGeral?.reenviavel && !isResetingPassword && (
          <button
            type="button"
            className="login-reenviar"
            disabled={isAwaitingAsyncEvent}
            onClick={() => {
              setIsAwaitingAsyncEvent(true);
              Firebase.reenviarConfirmacao(user, password)
                .then(() =>
                  setRecado({
                    campo: null,
                    tom: "aviso",
                    texto: `Mandamos de novo o e-mail de confirmação para ${user}. Abra a mensagem, clique no link e entre por aqui.`,
                  })
                )
                .catch((err) => setRecado(recadoDeEntrada(err?.code ?? "")))
                .finally(() => setIsAwaitingAsyncEvent(false));
            }}
          >
            Reenviar o e-mail de confirmação
          </button>
        )}

        {/* ── T29.5 · O E-MAIL ESTÁ ERRADO? ─────────────────────────────────────────────────────
            O relatório de 14/10: «E-mail não confirmado vira beco sem saída: permitir corrigir o
            e-mail ou dizer para criar outra conta». Reenviar só manda de novo para o MESMO
            endereço — quem errou uma letra no cadastro não tinha para onde ir. Agora há as duas
            saídas, ali mesmo. */}
        {(recadoGeral?.reenviavel || trocandoOEmail || recadoDoCampo("novo-email")) && !isResetingPassword && (
          <div className="login-email-errado">
            <p>
              <strong>O e-mail está errado, ou você não tem acesso a ele?</strong> Troque o
              endereço desta conta — o link de confirmação vai para o novo — ou{" "}
              <Link to="/cadastro">crie uma conta nova</Link>.
            </p>
            {!trocandoOEmail ? (
              <button type="button" className="login-reenviar" onClick={() => setTrocandoOEmail(true)} disabled={isAwaitingAsyncEvent}>
                Trocar o e-mail desta conta
              </button>
            ) : (
              <div className="field-container">
                <label htmlFor="novo-email">O e-mail certo</label>
                <input
                  id="novo-email"
                  type="email"
                  autoComplete="email"
                  value={novoEmail}
                  aria-invalid={!!recadoDoCampo("novo-email")}
                  aria-describedby={recadoDoCampo("novo-email") ? "novo-email-recado" : undefined}
                  onChange={(e) => {
                    limpaRecado();
                    setNovoEmail(e.target.value);
                  }}
                />
                {recadoDoCampo("novo-email") && (
                  <span className="login-recado-campo" role="alert" id="novo-email-recado">
                    {recadoDoCampo("novo-email")!.texto}
                  </span>
                )}
                <span className="login-email-errado-nota">
                  A senha continua a mesma. Para trocar, deixe preenchidos acima o e-mail antigo e a senha.
                </span>
                <button
                  type="button"
                  className="login-reenviar"
                  disabled={isAwaitingAsyncEvent || !novoEmail.trim()}
                  onClick={() => {
                    setIsAwaitingAsyncEvent(true);
                    Firebase.trocarOEmailNaoConfirmado(user, password, novoEmail)
                      .then(() => {
                        setRecado(recadoDaTroca("", novoEmail.trim()));
                        setTrocandoOEmail(false);
                        setUser(novoEmail.trim());
                        setNovoEmail("");
                      })
                      .catch((err) => setRecado(recadoDaTroca(err?.code ?? "")))
                      .finally(() => setIsAwaitingAsyncEvent(false));
                  }}
                >
                  Mandar o link para o e-mail certo
                </button>
              </div>
            )}
          </div>
        )}

        {!isResetingPassword && (
          <>
            <div className="field-container">
              <label htmlFor="usuario">E-mail</label>
              <input
                id="usuario"
                type="email"
                autoComplete="username"
                required
                aria-invalid={!!recadoDoCampo("usuario")}
                aria-describedby={recadoDoCampo("usuario") ? "usuario-recado" : undefined}
                value={user}
                onChange={(event) => {
                  limpaRecado();
                  setUser(event.target.value);
                }}
              />
              {recadoDoCampo("usuario") && (
                <span className="login-recado-campo" role="alert" id="usuario-recado">
                  {recadoDoCampo("usuario")!.texto}
                </span>
              )}
            </div>
            <div className="field-container">
              <label htmlFor="senha">Senha</label>
              {/* O TETO DA SENHA VEM DO MESMO LUGAR QUE O DO CADASTRO, e isto era um defeito
                  ATIVO que eu mesmo abri hoje: o cadastro passou a aceitar 64 caracteres no T8.14
                  e aqui continuava 15 — uma frase-senha criada hoje nao conseguiria ENTRAR
                  amanha. Dois campos que guardam a mesma senha com limites diferentes sao uma
                  porta que tranca por fora. */}
              <input
                id="senha"
                type="password"
                autoComplete="current-password"
                required
                minLength={SENHA.minimo}
                maxLength={SENHA.maximo}
                aria-invalid={!!recadoDoCampo("senha")}
                value={password}
                onChange={(event) => {
                  limpaRecado();
                  setPassword(event.target.value.trim());
                }}
              />
              {recadoDoCampo("senha") && (
                <span className="login-recado-campo" role="alert">
                  {recadoDoCampo("senha")!.texto}
                </span>
              )}
            </div>
          </>
        )}
        {isResetingPassword && (
          <div className="field-container">
            {/* O RÓTULO APONTAVA PARA UM `id` QUE NÃO EXISTE NESTA TELA: era
                `htmlFor="usuario"` sobre um input `id="email"`. É por isso que o relatório achou
                o campo "sem rótulo acessível" — havia um `<label>` visível, e ele não estava
                ligado a nada. Um caractere de diferença, e nenhum leitor de tela anunciava o
                campo. */}
            <label htmlFor="email">E-mail</label>
            <input
              id="email"
              type="email"
              autoComplete="username"
              required
              aria-invalid={!!recadoDoCampo("email")}
              aria-describedby={recadoDoCampo("email") ? "email-recado" : undefined}
              value={user}
              onChange={(event) => {
                limpaRecado();
                setUser(event.target.value);
              }}
            />
            {recadoDoCampo("email") && (
              <span className="login-recado-campo" role="alert" id="email-recado">
                {recadoDoCampo("email")!.texto}
              </span>
            )}
          </div>
        )}

        <div className="buttons-container">
          {/* T8.21 · OS MESMOS BOTÕES DO CADASTRO, pelas mesmas classes — e não um `<Button>` do MUI
              com `background-color: black`. Duas telas da mesma jornada com dois desenhos de botão é o
              que ele descreve como "bem diferente do cadastro", e a régua de 44 px vem de graça junto
              com a classe. */}
          <button
            className="conta-botao-principal"
            type="submit"
            disabled={isAwaitingAsyncEvent}
          >
            {isResetingPassword ? "Recuperar" : "Entrar"}
          </button>
          {!isResetingPassword && (
            // T8.21 · O AZUL DO GOOGLE SAIU. Ele era #4285F4 sobre preto — a cor de outra marca dentro
            // desta tela, e o contraste que ele notou. O botão continua sendo o do Google pelo que ele
            // FAZ e pelo nome, e não pela cor: quem entra por ali lê "Entrar com Google".
            <button
              type="button"
              className="conta-botao-secundario"
              disabled={isAwaitingAsyncEvent}
              onClick={() => {
                setIsAwaitingAsyncEvent(true);
                setRecado(null);
                Firebase.loginWithGoogle()
                  .catch((err) => setRecado(recadoDeEntrada(err?.code ?? "")))
                  .finally(() => setIsAwaitingAsyncEvent(false));
              }}
            >
              Entrar com Google
            </button>
          )}

          {/* ERA UM `<span onClick>`: sem Tab, sem foco, invisível para teclado e para leitor de
              tela. É o mesmo defeito que o "Excluir conta" da tela de conta tinha, e o conserto é
              o mesmo — botão de verdade. (Isto pertence ao T8.21, e entra aqui porque deixar um
              `<span>` clicável no meio de uma tela que eu estava reescrevendo por acessibilidade
              seria consertar a metade que aparece no relatório.) */}
          <button type="button" className="login-trocar-modo" onClick={trocaDeModo}>
            {isResetingPassword ? "Voltar" : "Esqueci a senha"}
          </button>
        </div>
      </form>
    </div>
  );
}

export default Login;
